/**
 * applyTagsToRows
 *
 * Injects selected tag names into the `contact_label` column for every
 * included (non-excluded) row, so the import engine creates/links them as
 * ContactTags during `start_import_contacts`.
 *
 * Strategy:
 *  1. Locate an existing header mapped to `contact_label`. If none exists,
 *     add a synthetic header column `__contact_label__` mapped to `contact_label`.
 *  2. For each included row, join `tagNames` with any already-present label
 *     value using a comma, deduplicating blanks.
 *  3. Excluded rows are left untouched.
 *
 * This is a pure function — it never mutates its input.
 */
import { ImportData, InternalColumns } from 'features/importerv2/importer-types'

const SYNTHETIC_LABEL_ORIGINAL = '__contact_label__'

/**
 * Apply tag names to included rows' `contact_label` column.
 *
 * @param data         - The current ImportData (already marked via markRowsCustomOther).
 * @param includedRowIds - String row IDs that should receive the tags.
 * @param tagNames     - Tag name strings to inject (may be empty).
 * @returns A new ImportData with the label column populated.
 */
export function applyTagsToRows(
  data: ImportData,
  includedRowIds: ReadonlySet<string>,
  tagNames: string[]
): ImportData {
  if (tagNames.length === 0) return data

  // Find the original column key for the header mapped to 'contact_label'.
  let labelOriginal: string | undefined = data.headers.find(
    (h) => h.mapped === 'contact_label'
  )?.original

  let headers = data.headers

  // If there is no existing contact_label header, add a synthetic one.
  if (!labelOriginal) {
    labelOriginal = SYNTHETIC_LABEL_ORIGINAL
    headers = [
      ...headers,
      {
        original: SYNTHETIC_LABEL_ORIGINAL,
        mapped: 'contact_label',
        label: 'Label',
      },
    ]
  }

  const labelKey = labelOriginal
  const isSynthetic = labelKey === SYNTHETIC_LABEL_ORIGINAL

  const rows = data.rows.map((row) => {
    const rowId = String(row[InternalColumns.ROW_ID])

    if (!includedRowIds.has(rowId)) {
      // For the synthetic column, ensure the key exists on every row (including
      // excluded ones) so toRowsJSON — which derives column list from
      // Object.keys(rows[0]) — always finds the column even when row[0] is
      // excluded. Use the row's existing value if present, otherwise ''.
      if (isSynthetic && !(labelKey in row)) {
        return { ...row, [labelKey]: '' }
      }
      return row
    }

    const existing = row[labelKey]
    const existingStr =
      existing != null && String(existing).trim() !== ''
        ? String(existing).trim()
        : ''

    // Merge any already-present labels with the selected tag names, dropping
    // blanks and case-sensitive duplicates so a tag the row already carries
    // (user-mapped or from a prior run) isn't written twice ("VIP,VIP").
    const existingTags = existingStr
      ? existingStr
          .split(',')
          .map((t) => t.trim())
          .filter((t) => t !== '')
      : []
    const combined = [...new Set([...existingTags, ...tagNames])].join(',')

    return { ...row, [labelKey]: combined }
  })

  return { headers, rows }
}
